/**
 * StoryGrid component - Grid layout for displaying user's stories on profile
 */

import { Loader2, Heart, MessageCircle, Play, Layers } from 'lucide-react';
import type { Story } from '../../types';
import { Alert, AlertDescription } from '../ui/alert';

interface StoryGridProps {
  stories: Story[];
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onStoryClick?: (storyId: string) => void;
}

export const StoryGrid = ({ stories, isLoading, isError, error, onStoryClick }: StoryGridProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading stories...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load stories. {error?.message}</AlertDescription>
      </Alert>
    );
  }

  if (stories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
        <div className="text-6xl">📷</div>
        <h3 className="text-lg font-semibold">No stories yet</h3>
        <p className="max-w-sm text-sm text-muted-foreground">
          Stories uploaded by this user will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-1 md:gap-2">
      {stories.map((story) => {
        // Get the first photo from the story for the thumbnail
        const firstPhoto = story.photos[0];
        const isVideo = firstPhoto?.mediaType === 'video';
        const hasMultipleItems = story.photoCount > 1;

        return (
          <button
            key={story.storyId}
            onClick={() => onStoryClick?.(story.storyId)}
            className="group relative aspect-square overflow-hidden bg-muted"
          >
            {/* Thumbnail */}
            <img
              src={firstPhoto?.images.thumbnail || ''}
              alt={story.caption || 'Story'}
              className="h-full w-full object-cover"
              loading="lazy"
            />

            {/* Video indicator */}
            {isVideo && (
              <div className="absolute right-2 top-2">
                <Play className="h-4 w-4 fill-white text-white drop-shadow-md" />
              </div>
            )}

            {/* Multiple items indicator */}
            {hasMultipleItems && (
              <div className="absolute right-2 top-2">
                <Layers className="h-4 w-4 fill-white text-white drop-shadow-md" />
              </div>
            )}

            {/* Hover overlay with engagement stats */}
            <div className="absolute inset-0 flex items-center justify-center gap-6 bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100">
              <div className="flex items-center gap-2">
                <Heart className="h-5 w-5 fill-current" />
                <span className="font-semibold">{story.likeCount}</span>
              </div>
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5 fill-current" />
                <span className="font-semibold">{story.commentCount}</span>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};
